import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;

class BaseActionCableConnector {
  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.app = app;
    this.events = {};
    this.isDisconnected = false;
    this.isDisposed = false;
    this.isConnected = false;
    this.presenceActive = true;
    this.presenceTimer = null;
    this.reconnectTimer = null;
    this.lastPresenceAt = null;
    this.lastRecoveryAt = null;
    this.presenceInterval = presenceInterval;
    this.isAValidEvent = () => true;
    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          if (this.isDisposed) return;
          this.isConnected = true;
          this.lastPresenceAt = Date.now();
          this.refreshPresence();
          if (this.isDisconnected) {
            this.isDisconnected = false;
            this.onReconnect();
          }
        },
        disconnected: () => {
          this.isConnected = false;
          clearTimeout(this.presenceTimer);
          if (this.isDisposed || this.isDisconnected) return;
          this.isDisconnected = true;
          this.onDisconnected();
        },
      }
    );
  }

  triggerPresenceInterval = () => {
    clearTimeout(this.presenceTimer);
    this.presenceTimer = setTimeout(
      this.refreshPresence,
      this.presenceInterval
    );
  };

  refreshPresence = () => {
    if (this.isDisposed || !this.isConnected || !this.presenceActive) return;

    // Cable pings bypass Redis. A presence response proves the full pubsub path.
    if (Date.now() - this.lastPresenceAt >= this.presenceInterval * 3) {
      this.recoverStaleConnection();
      return;
    }

    this.subscription.updatePresence();
    this.triggerPresenceInterval();
  };

  recoverStaleConnection = () => {
    this.isConnected = false;
    if (!this.isDisconnected) {
      this.isDisconnected = true;
      this.onDisconnected({ disconnectedAt: this.lastPresenceAt });
    }
    this.consumer.disconnect();
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(
      () => {
        if (!this.isDisposed) this.consumer.connect();
      },
      500 + Math.random() * 1000
    );
  };

  setPresenceActive(isActive) {
    this.presenceActive = isActive;
    clearTimeout(this.presenceTimer);
    if (this.isDisposed || !isActive) return;

    this.consumer.ensureActiveConnection();
    this.refreshPresence();
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.isDisposed = true;
    this.isConnected = false;
    clearTimeout(this.presenceTimer);
    clearTimeout(this.reconnectTimer);
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isDisposed) return;
    if (event === 'realtime.recovered') {
      if (this.lastRecoveryAt === data.disconnected_at) return;
      this.lastRecoveryAt = data.disconnected_at;
      // Redis can reconnect without closing the WebSocket. Its missed events
      // still need the same HTTP catch-up as a browser disconnection.
      this.onDisconnected({ disconnectedAt: data.disconnected_at });
      this.onReconnect();
      return;
    }
    if (this.isAValidEvent(data)) {
      if (event === 'presence.update') this.lastPresenceAt = Date.now();
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
